import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {loadScript} from '../../../../analytics/utils';
import {
  getStripeSessionClientSecret,
  getBillingPortalLink,
} from '../../promises';

import env from '../../../../environment';
import {makeStyles, Typography} from '@material-ui/core';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import {useToastActions} from '../../../toast-alert/utils';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useCurrentSubscription} from '../../utils';
import {getStringDate} from '../../../../utils/date';
import SwitchTabs from '../../../../elements/SwitchTabs/SwitchTabs';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    padding: '1rem',
    maxWidth: '1300px',
    background: '#F8F9FB',
    flexDirection: 'column',
  },
  header: {
    alignSelf: 'flex-start',
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    marginTop: '2rem',
  },

  manageSubscriptionButton: {
    borderRadius: '.75rem',
    textTransform: 'none',
  },

  title: {
    fontWeight: 600,
    textAlign: 'left',
  },
  pricingTableContainer: {
    justifyContent: 'center',
    width: '100%',
    borderRadius: '1rem',
    overflow: 'hidden',
    minHeight: '90vh',
    padding: '1rem',
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
    '& > stripe-pricing-table': {
      width: '100%',
    },
  },
  tabsContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1rem',
  },
  pricingTableHidden: {
    display: 'none',
  },
  planDescription: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
}));

const PLAN_TABS = [
  {label: 'Plan Standard', value: 'default'},
  {label: 'IA', value: 'ai_plans'},
];

export default function StripeTablePrice() {
  const classes = useStyles();
  const subscription = useCurrentSubscription();
  const [selectedTab, setSelectedTab] = useState('default');

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const [
    customerSessionClientSecret,
    setCustomerSessionClientSecret,
  ] = useState();

  const [billingLink, setBillingLink] = useState('');

  const {showToast} = useToastActions();

  const history = useHistory();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (subscription) return;
    loadScript('https://js.stripe.com/v3/pricing-table.js', {
      async: true,
    });
    setLoading(true);

    getStripeSessionClientSecret()
      .then(clientSecret => {
        setCustomerSessionClientSecret(clientSecret.clientSecret);
      })
      .catch(error => {
        showToast({
          message: 'Ocurrió un error al cargar los planes de TiendaDa',
          title: 'Error',
          type: 'danger',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [history, showToast, subscription]);

  useEffect(() => {
    if (!subscription) return;
    getBillingPortalLink(window.location.href)
      .then(response => {
        setBillingLink(response.url);
      })
      .catch(() => {
        showToast({
          message: 'Ocurrió un error al cargar los planes de TiendaDa',
          title: 'Error',
          type: 'danger',
        });
      });
  }, [showToast, subscription]);

  return (
    <div className={classes.container}>
      {subscription && (
        <div className={classes.header}>
          <div>
            <div className={classes.planDescription}>
              <Typography component="h5" variant="h5" className={classes.title}>
                {subscription?.storeTierPlan?.storeTier?.label}
              </Typography>
              {`  -  `}
              <Typography variant="body1">
                Suscripción {subscription?.storeTierPlan?.frequency?.label}
              </Typography>
            </div>
            <div className={classes.planDescription}>
              <Typography
                component="p"
                variant="body1"
                className={classes.title}
              >
                {subscription?.subscriptionPrice}
              </Typography>
              {` | `}
              <Typography variant="body2">
                Proximo cargo: {getStringDate(subscription?.endDate)}
              </Typography>
            </div>
          </div>

          <CustomButton
            variant="contained"
            color="primary"
            component="a"
            href={billingLink}
            className={classes.manageSubscriptionButton}
          >
            Gestionar Suscripción
          </CustomButton>
        </div>
      )}
      <div className={classes.header}>
        <Typography component="h5" variant="h5" className={classes.title}>
          Planes de TiendaDa
        </Typography>
      </div>
      <div className={classes.tabsContainer}>
        <SwitchTabs
          value={selectedTab}
          onChange={handleTabChange}
          labels={PLAN_TABS}
        />
      </div>
      <div className={classes.pricingTableContainer}>
        {loading && <InlineLoader loading size={40} />}
        {customerSessionClientSecret &&
          Object.entries(env.stripePricingTables).map(([key, table]) => (
            <div
              key={key}
              className={
                selectedTab !== key ? classes.pricingTableHidden : undefined
              }
            >
              <stripe-pricing-table
                pricing-table-id={table.tableId}
                publishable-key={table.publishableKey}
                customer-session-client-secret={customerSessionClientSecret}
              ></stripe-pricing-table>
            </div>
          ))}
      </div>
    </div>
  );
}
